import { createContext, useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useLogin, useLogout, useLocalStorage } from '@/hooks'
import type { TCredenciaisLogin, TLoginErrorResponse, TUsuarioLogado } from '@/http'
import type { IHttpResponse } from '@/infra/http/http-service'
import { setLocalidade } from '@/infra/i18n'

type TAuthContext = {
  user: TUsuarioLogado | null
  login: (credenciais: TCredenciaisLogin) => Promise<TUsuarioLogado>
  logout: () => Promise<IHttpResponse<{ message: string }>>
  reset: VoidFunction
  state: {
    error: TLoginErrorResponse | null
    isLogando: boolean
    isAuthenticated: boolean
  }
  setError: (error: TLoginErrorResponse | null) => void
}

const AuthContext = createContext<TAuthContext>({
  user: null,
  state: { isAuthenticated: false, isLogando: false, error: null },
  reset: () => null,
  login: () => Promise.resolve({} as TUsuarioLogado),
  logout: () => Promise.resolve({} as IHttpResponse<{ message: string }>),
  setError: () => null,
})

const LOGIN_ERROR_CODES = ['INVALID_CREDENTIALS', 'UNAUTHORIZED_USER_PROFILE', 'SERVER_COMMUNICATION_FAILURE'] as const

const AuthProvider = ({ children }: PropsWithChildren) => {
  const [error, setError] = useState<TLoginErrorResponse | null>(null)
  const [user, setUser] = useLocalStorage<TUsuarioLogado | null>('user', null)
  const { mutateAsync, reset, isPending } = useLogin()
  const { mutateAsync: mutateAsyncLogout } = useLogout()
  const navigate = useNavigate()
  const { t } = useTranslation('login')
  const isAuthenticated = Boolean(user?.usuarioLogadoId)

  const login = useCallback(
    async (credenciais: TCredenciaisLogin) =>
      await mutateAsync(credenciais, {
        onSuccess: (response) => {
          setUser(response)
          setLocalidade(response.localidade)

          navigate('/', { replace: true })
        },
        onError: (_error) => {
          if (!_error?.code) return

          const codigoErroApi = _error?.response?.data?.codigoErro
          const codigoErroMapeado = LOGIN_ERROR_CODES.find((code) => code === codigoErroApi)

          if (codigoErroMapeado) {
            setError({
              titulo: t(`errors.${codigoErroMapeado}.titulo`),
              descricao: t(`errors.${codigoErroMapeado}.descricao`),
            })
            return
          }

          setError({ descricao: _error.code, titulo: _error?.message })
        },
      }),
    [mutateAsync, navigate, setUser, t],
  )

  const logout = useCallback(
    async () =>
      mutateAsyncLogout(undefined, {
        onSuccess: () => {
          setUser(null)
          setLocalidade(null).finally(() => navigate('/login', { replace: true }))
        },
      }),
    [mutateAsyncLogout, navigate, setUser],
  )

  const contextValue = useMemo(
    () => ({
      user,
      login,
      logout,
      reset,
      setError,
      state: { error, isLogando: isPending, isAuthenticated },
    }),
    [user, login, logout, reset, error, isPending, isAuthenticated],
  )

  useEffect(() => {
    if (user?.localidade) setLocalidade(user.localidade)
  }, [user?.localidade])

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
